/**
 * Workboat Tasks Data
 *
 * 47 pre-defined workboat tasks aligned with WBC3 (Workboat Code 3).
 * These tasks are available in the Lite tier for workboat operators.
 *
 * @module data/workboatTasks
 */

/**
 * Task categories
 */
export const TASK_CATEGORIES = {
  DECK_OPERATIONS: 'Deck Operations',
  ENGINEERING_OPERATIONS: 'Engineering Operations',
  NAVIGATION_BRIDGE: 'Navigation & Bridge',
  MOORING_ANCHORING: 'Mooring & Anchoring',
  CREW_WELFARE: 'Crew Welfare',
  EMERGENCY_DRILL_SAFETY: 'Emergency Drill Safety',
  CYBER_SECURITY: 'Cyber Security'
};

/**
 * 47 Workboat-specific tasks aligned with WBC3
 */
export const workboatTasks = [
  // =========================================================================
  // DECK OPERATIONS (12 tasks)
  // =========================================================================
  {
    id: 'WB-DECK-001',
    category: TASK_CATEGORIES.DECK_OPERATIONS,
    title: 'Launching/Recovering Small Boats',
    description: 'Operating davits, cranes, or other launching equipment to deploy and recover small boats, RIBs, or rescue craft.',
    commonHazards: [
      'Crush injuries from moving equipment',
      'Falls overboard during recovery',
      'Equipment failure (wire/rope parting)',
      'Man overboard during launch',
      'Swinging loads'
    ],
    suggestedControls: [
      'Pre-use inspection of davits and lifting equipment',
      'PPE (lifejackets, safety harness)',
      'Clear communication procedures',
      'Adequate lighting for night operations',
      'Trained personnel only'
    ],
    tier: 'lite',
    wbc3Reference: 'WBC3 Section 18.4'
  },
  {
    id: 'WB-DECK-002',
    category: TASK_CATEGORIES.DECK_OPERATIONS,
    title: 'Deck Cargo Handling',
    description: 'Loading, securing, and unloading deck cargo including containers, equipment, and materials.',
    commonHazards: [
      'Crush injuries from falling cargo',
      'Musculoskeletal injuries from manual handling',
      'Slips and trips on deck',
      'Cargo shift in heavy weather',
      'Struck by swinging loads'
    ],
    suggestedControls: [
      'Proper securing arrangements (lashings, chains)',
      'Safe working load limits observed',
      'Weather routing to avoid heavy seas',
      'PPE (safety boots, hard hats, gloves)',
      'Cargo securing manual followed'
    ],
    tier: 'lite',
    wbc3Reference: 'WBC3 Section 15'
  },
  {
    id: 'WB-DECK-003',
    category: TASK_CATEGORIES.DECK_OPERATIONS,
    title: 'Crane Operations',
    description: 'Operating vessel-mounted cranes for cargo, equipment, or personnel transfer.',
    commonHazards: [
      'Overloading crane',
      'Swinging loads striking personnel',
      'Electrical contact (overhead cables)',
      'Mechanical failure',
      'Operator fatigue'
    ],
    suggestedControls: [
      'Current crane certification',
      'Trained and certified operators',
      'Load charts visible and followed',
      'Exclusion zones during lifting',
      'Regular maintenance and inspection'
    ],
    tier: 'lite',
    wbc3Reference: 'WBC3 Section 15.3'
  },
  {
    id: 'WB-DECK-004',
    category: TASK_CATEGORIES.DECK_OPERATIONS,
    title: 'Working at Height',
    description: 'Work carried out on masts, superstructure, or other elevated positions.',
    commonHazards: [
      'Falls from height',
      'Dropped tools/objects',
      'Vessel movement causing loss of balance',
      'Weather exposure (wind, rain, cold)',
      'Electrical hazards'
    ],
    suggestedControls: [
      'Full body harness with double lanyard',
      'Tool lanyards for all equipment',
      'Avoid work at height in poor weather',
      'Ground crew safety helmets',
      'Permit to work system'
    ],
    tier: 'lite',
    wbc3Reference: 'WBC3 Section 23.5'
  },
  {
    id: 'WB-DECK-005',
    category: TASK_CATEGORIES.DECK_OPERATIONS,
    title: 'Rope and Wire Handling',
    description: 'Handling mooring ropes, tow wires, and other running gear.',
    commonHazards: [
      'Rope snap-back injuries',
      'Hand/finger entrapment',
      'Wire splinter injuries',
      'Trips on loose ropes',
      'Burns from running ropes'
    ],
    suggestedControls: [
      'Snap-back zones identified and marked',
      'Proper PPE (gloves, safety footwear)',
      'Regular inspection of ropes and wires',
      'Personnel clear during tensioning',
      'Training in rope handling'
    ],
    tier: 'lite',
    wbc3Reference: 'WBC3 Section 14'
  },
  {
    id: 'WB-DECK-006',
    category: TASK_CATEGORIES.DECK_OPERATIONS,
    title: 'Personnel Transfer Operations',
    description: 'Transferring personnel between vessels or to/from offshore installations.',
    commonHazards: [
      'Falls during transfer',
      'Crush injuries between vessels',
      'Man overboard',
      'Equipment failure',
      'Vessel collision'
    ],
    suggestedControls: [
      'Gangway/transfer system certified and inspected',
      'Personnel wearing lifejackets',
      'Good communication between vessels',
      'Weather limits established and observed',
      'Emergency recovery procedures in place'
    ],
    tier: 'lite',
    wbc3Reference: 'WBC3 Section 21'
  },
  {
    id: 'WB-DECK-007',
    category: TASK_CATEGORIES.DECK_OPERATIONS,
    title: 'Diving Support Operations',
    description: 'Supporting diving operations from the vessel.',
    commonHazards: [
      'Diver injury/incident',
      'Propeller strike',
      'Equipment failure',
      'Communication failure',
      'Environmental conditions'
    ],
    suggestedControls: [
      'Diving operations manual followed',
      'Propeller lockout procedures',
      'Dive flag displayed',
      'Standby diver available',
      'Emergency procedures established'
    ],
    tier: 'lite',
    wbc3Reference: 'WBC3 Section 20'
  },
  {
    id: 'WB-DECK-008',
    category: TASK_CATEGORIES.DECK_OPERATIONS,
    title: 'Towing Operations',
    description: 'Conducting towing operations including connecting/disconnecting tow.',
    commonHazards: [
      'Tow wire snap-back',
      'Girting/capsizing',
      'Collision with tow',
      'Personnel struck by equipment',
      'Loss of tow'
    ],
    suggestedControls: [
      'Towing arrangements certified',
      'Snap-back zones established',
      'Emergency release procedures',
      'Clear communication between vessels',
      'Weather limits observed'
    ],
    tier: 'lite',
    wbc3Reference: 'WBC3 Section 17'
  },
  {
    id: 'WB-DECK-009',
    category: TASK_CATEGORIES.DECK_OPERATIONS,
    title: 'Anchor Handling Operations',
    description: 'Handling anchors including deployment, recovery, and chain work.',
    commonHazards: [
      'Crush injuries from anchor/chain',
      'Entanglement in chain',
      'Equipment failure',
      'Falls on slippery deck',
      'Anchor dragging'
    ],
    suggestedControls: [
      'Anchor handling procedures followed',
      'PPE (safety boots, gloves, hard hat)',
      'Exclusion zones during operations',
      'Equipment inspected before use',
      'Adequate manning levels'
    ],
    tier: 'lite',
    wbc3Reference: 'WBC3 Section 16'
  },
  {
    id: 'WB-DECK-010',
    category: TASK_CATEGORIES.DECK_OPERATIONS,
    title: 'Hot Work on Deck',
    description: 'Welding, cutting, or grinding operations on deck areas.',
    commonHazards: [
      'Fire and explosion',
      'Burns from hot metal/sparks',
      'Eye damage from UV/bright light',
      'Fume inhalation',
      'Electric shock'
    ],
    suggestedControls: [
      'Hot work permit system',
      'Fire watch maintained',
      'Fire extinguishers at hand',
      'Proper PPE (welding mask, gloves, apron)',
      'Ventilation for enclosed spaces'
    ],
    tier: 'lite',
    wbc3Reference: 'WBC3 Section 23.4'
  },
  {
    id: 'WB-DECK-011',
    category: TASK_CATEGORIES.DECK_OPERATIONS,
    title: 'Painting and Surface Preparation',
    description: 'Painting, coating application, and surface preparation activities.',
    commonHazards: [
      'Chemical exposure (solvents, paints)',
      'Falls from staging/scaffolding',
      'Fire risk from flammable materials',
      'Respiratory hazards',
      'Skin sensitization'
    ],
    suggestedControls: [
      'Material safety data sheets reviewed',
      'Proper ventilation',
      'PPE (respirator, gloves, coveralls)',
      'No smoking/naked flames',
      'Safe access equipment'
    ],
    tier: 'lite',
    wbc3Reference: 'WBC3 Section 23.6'
  },
  {
    id: 'WB-DECK-012',
    category: TASK_CATEGORIES.DECK_OPERATIONS,
    title: 'Working with Hazardous Substances',
    description: 'Handling chemicals, fuels, oils, and other hazardous materials.',
    commonHazards: [
      'Chemical burns/skin contact',
      'Inhalation of vapours',
      'Fire/explosion risk',
      'Environmental pollution',
      'Long-term health effects'
    ],
    suggestedControls: [
      'COSHH assessments completed',
      'Proper PPE for substance handled',
      'Spill containment measures',
      'First aid equipment available',
      'Training in safe handling'
    ],
    tier: 'lite',
    wbc3Reference: 'WBC3 Section 23.7'
  },

  // =========================================================================
  // ENGINEERING OPERATIONS (8 tasks)
  // =========================================================================
  {
    id: 'WB-ENG-001',
    category: TASK_CATEGORIES.ENGINEERING_OPERATIONS,
    title: 'Engine Room Operations',
    description: 'Routine engine room watchkeeping and machinery operations.',
    commonHazards: [
      'Burns from hot surfaces',
      'Noise-induced hearing damage',
      'Slips on oily surfaces',
      'Entanglement in rotating machinery',
      'Exposure to exhaust fumes'
    ],
    suggestedControls: [
      'Engine room procedures followed',
      'PPE (ear protection, safety footwear)',
      'Good housekeeping maintained',
      'Guards on rotating equipment',
      'Adequate ventilation'
    ],
    tier: 'lite',
    wbc3Reference: 'WBC3 Section 10'
  },
  {
    id: 'WB-ENG-002',
    category: TASK_CATEGORIES.ENGINEERING_OPERATIONS,
    title: 'Fuel Transfer Operations',
    description: 'Bunkering and internal fuel transfer operations.',
    commonHazards: [
      'Fire and explosion',
      'Pollution from spills',
      'Slip hazards from spillage',
      'Vapour inhalation',
      'Overfilling tanks'
    ],
    suggestedControls: [
      'Bunkering checklist completed',
      'Deck scuppers plugged',
      'No smoking during operations',
      'Communication with supplier',
      'Spill response equipment ready'
    ],
    tier: 'lite',
    wbc3Reference: 'WBC3 Section 10.4'
  },
  {
    id: 'WB-ENG-003',
    category: TASK_CATEGORIES.ENGINEERING_OPERATIONS,
    title: 'Electrical System Work',
    description: 'Maintenance and repair of electrical systems and equipment.',
    commonHazards: [
      'Electric shock',
      'Arc flash burns',
      'Fire from electrical faults',
      'Equipment damage',
      'Falls during access'
    ],
    suggestedControls: [
      'Isolation procedures followed',
      'Lock out/tag out system',
      'Insulated tools used',
      'Permit to work for high voltage',
      'Qualified personnel only'
    ],
    tier: 'lite',
    wbc3Reference: 'WBC3 Section 10.5'
  },
  {
    id: 'WB-ENG-004',
    category: TASK_CATEGORIES.ENGINEERING_OPERATIONS,
    title: 'Confined Space Entry',
    description: 'Entry into tanks, void spaces, and other confined areas.',
    commonHazards: [
      'Oxygen deficiency',
      'Toxic atmosphere',
      'Entrapment',
      'Fire/explosion',
      'Difficulty of rescue'
    ],
    suggestedControls: [
      'Enclosed space entry permit',
      'Atmosphere testing before and during entry',
      'Standby person at all times',
      'Rescue equipment ready',
      'Continuous ventilation'
    ],
    tier: 'lite',
    wbc3Reference: 'WBC3 Section 23.3'
  },
  {
    id: 'WB-ENG-005',
    category: TASK_CATEGORIES.ENGINEERING_OPERATIONS,
    title: 'Hydraulic System Maintenance',
    description: 'Working on hydraulic systems including pumps, cylinders, and pipework.',
    commonHazards: [
      'High pressure fluid injection',
      'Burns from hot hydraulic fluid',
      'Slips from oil spillage',
      'Crushing from moving parts',
      'Eye injuries from spray'
    ],
    suggestedControls: [
      'System depressurized before work',
      'PPE (safety glasses, gloves)',
      'Spill containment',
      'Proper disposal of waste oil',
      'Inspection for leaks before use'
    ],
    tier: 'lite',
    wbc3Reference: 'WBC3 Section 10.6'
  },
  {
    id: 'WB-ENG-006',
    category: TASK_CATEGORIES.ENGINEERING_OPERATIONS,
    title: 'Battery Maintenance',
    description: 'Maintenance of battery systems including charging and watering.',
    commonHazards: [
      'Acid burns from electrolyte',
      'Explosion from hydrogen gas',
      'Electric shock',
      'Manual handling injuries',
      'Short circuit fires'
    ],
    suggestedControls: [
      'Ventilation during charging',
      'No smoking or naked flames',
      'PPE (goggles, acid-resistant gloves)',
      'Eye wash station nearby',
      'Correct tools (insulated)'
    ],
    tier: 'lite',
    wbc3Reference: 'WBC3 Section 10.5'
  },
  {
    id: 'WB-ENG-007',
    category: TASK_CATEGORIES.ENGINEERING_OPERATIONS,
    title: 'Generator Operations',
    description: 'Operation and maintenance of generators and switchboards.',
    commonHazards: [
      'Electric shock',
      'Burns from hot components',
      'Noise exposure',
      'Fire from electrical faults',
      'Carbon monoxide poisoning'
    ],
    suggestedControls: [
      'Operating procedures followed',
      'Regular maintenance schedule',
      'Guards and covers in place',
      'CO detection in enclosed spaces',
      'Emergency shutdown procedures known'
    ],
    tier: 'lite',
    wbc3Reference: 'WBC3 Section 10.5'
  },
  {
    id: 'WB-ENG-008',
    category: TASK_CATEGORIES.ENGINEERING_OPERATIONS,
    title: 'Steering Gear Maintenance',
    description: 'Maintenance and testing of steering gear systems.',
    commonHazards: [
      'Crushing from moving parts',
      'Hydraulic fluid injection',
      'Loss of steering during operations',
      'Electric shock from controls',
      'Confined space hazards'
    ],
    suggestedControls: [
      'Testing at sea with caution',
      'Isolation before maintenance',
      'Communication with bridge',
      'Guards replaced after work',
      'Emergency steering tested'
    ],
    tier: 'lite',
    wbc3Reference: 'WBC3 Section 10.3'
  },

  // =========================================================================
  // NAVIGATION & BRIDGE (10 tasks)
  // =========================================================================
  {
    id: 'WB-NAV-001',
    category: TASK_CATEGORIES.NAVIGATION_BRIDGE,
    title: 'Bridge Watchkeeping',
    description: 'Maintaining a proper navigational watch at sea.',
    commonHazards: [
      'Collision with other vessels',
      'Grounding',
      'Fatigue affecting judgment',
      'Equipment malfunction',
      'Weather deterioration'
    ],
    suggestedControls: [
      'Proper lookout maintained',
      'Bridge procedures followed',
      'Rest periods observed',
      'Equipment checks at watch change',
      'Weather monitoring'
    ],
    tier: 'lite',
    wbc3Reference: 'WBC3 Section 11'
  },
  {
    id: 'WB-NAV-002',
    category: TASK_CATEGORIES.NAVIGATION_BRIDGE,
    title: 'Pilotage and Port Entry',
    description: 'Navigating in pilotage waters and entering/leaving port.',
    commonHazards: [
      'Collision in confined waters',
      'Grounding in shallow areas',
      'Allision with structures',
      'Interaction with other traffic',
      'Communication failures'
    ],
    suggestedControls: [
      'Passage plan reviewed',
      'Pilot card prepared',
      'Anchor ready for deployment',
      'Engine on standby',
      'VHF communications confirmed'
    ],
    tier: 'lite',
    wbc3Reference: 'WBC3 Section 11.3'
  },
  {
    id: 'WB-NAV-003',
    category: TASK_CATEGORIES.NAVIGATION_BRIDGE,
    title: 'Navigation in Restricted Visibility',
    description: 'Operating the vessel in fog, heavy rain, or other conditions limiting visibility.',
    commonHazards: [
      'Collision with unseen vessels',
      'Grounding without visual references',
      'Delayed recognition of hazards',
      'Radar target confusion',
      'VHF channel congestion'
    ],
    suggestedControls: [
      'Reduced speed as per COLREGs',
      'Fog signals sounded',
      'Radar plotting enhanced',
      'Extra lookout posted',
      'Engine on standby'
    ],
    tier: 'lite',
    wbc3Reference: 'WBC3 Section 11.2'
  },
  {
    id: 'WB-NAV-004',
    category: TASK_CATEGORIES.NAVIGATION_BRIDGE,
    title: 'Heavy Weather Operations',
    description: 'Operating in severe weather conditions including storms.',
    commonHazards: [
      'Vessel stability concerns',
      'Cargo shift',
      'Crew injuries from vessel motion',
      'Structural damage',
      'Equipment damage'
    ],
    suggestedControls: [
      'Heavy weather preparations made',
      'Cargo securing checked',
      'Speed reduced as necessary',
      'Crew movements restricted',
      'Weather routing utilized'
    ],
    tier: 'lite',
    wbc3Reference: 'WBC3 Section 13'
  },
  {
    id: 'WB-NAV-005',
    category: TASK_CATEGORIES.NAVIGATION_BRIDGE,
    title: 'Night Navigation',
    description: 'Navigating the vessel during hours of darkness.',
    commonHazards: [
      'Difficulty identifying lights',
      'Fatigue in watch personnel',
      'Reduced visibility of hazards',
      'Disorientation',
      'Light pollution confusion'
    ],
    suggestedControls: [
      'Navigation lights verified working',
      'Bridge lighting adjusted',
      'Extra vigilance on lookout',
      'Binoculars for light identification',
      'Radar use enhanced'
    ],
    tier: 'lite',
    wbc3Reference: 'WBC3 Section 11'
  },
  {
    id: 'WB-NAV-006',
    category: TASK_CATEGORIES.NAVIGATION_BRIDGE,
    title: 'Dynamic Positioning Operations',
    description: 'Operating vessel using DP systems for station keeping.',
    commonHazards: [
      'DP system failure',
      'Thruster malfunction',
      'Position excursion',
      'Collision with structure',
      'Power management issues'
    ],
    suggestedControls: [
      'DP checklist completed',
      'FMEA reviewed',
      'Escape routes planned',
      'Weather limits set',
      'Continuous position monitoring'
    ],
    tier: 'lite',
    wbc3Reference: 'WBC3 Section 19'
  },
  {
    id: 'WB-NAV-007',
    category: TASK_CATEGORIES.NAVIGATION_BRIDGE,
    title: 'Manoeuvring Alongside',
    description: 'Coming alongside quay, installation, or another vessel.',
    commonHazards: [
      'Collision/allision damage',
      'Personnel injury between vessel and structure',
      'Mooring line failures',
      'Fendering inadequacy',
      'Misjudged distances'
    ],
    suggestedControls: [
      'Approach plan discussed',
      'Fendering adequate',
      'Mooring stations manned',
      'Communication clear between bridge and deck',
      'Engines available for manoeuvring'
    ],
    tier: 'lite',
    wbc3Reference: 'WBC3 Section 14'
  },
  {
    id: 'WB-NAV-008',
    category: TASK_CATEGORIES.NAVIGATION_BRIDGE,
    title: 'ECDIS/Chart Operations',
    description: 'Using electronic charts and navigation systems.',
    commonHazards: [
      'Over-reliance on electronics',
      'Chart data errors',
      'System failures',
      'Incorrect settings',
      'Update management failures'
    ],
    suggestedControls: [
      'Cross-checking with radar/visual',
      'Regular updates installed',
      'Alarms properly set',
      'Paper chart backup available',
      'Training in system operation'
    ],
    tier: 'lite',
    wbc3Reference: 'WBC3 Section 11.1'
  },
  {
    id: 'WB-NAV-009',
    category: TASK_CATEGORIES.NAVIGATION_BRIDGE,
    title: 'Communications Management',
    description: 'Managing VHF, MF/HF, satellite, and internal communications.',
    commonHazards: [
      'Communication failures',
      'Misunderstandings/language issues',
      'Emergency signal failures',
      'Equipment malfunction',
      'Distress alerting failures'
    ],
    suggestedControls: [
      'Regular equipment testing',
      'Standard phrases used',
      'Emergency procedures practised',
      'Backup systems available',
      'GMDSS procedures followed'
    ],
    tier: 'lite',
    wbc3Reference: 'WBC3 Section 12'
  },
  {
    id: 'WB-NAV-010',
    category: TASK_CATEGORIES.NAVIGATION_BRIDGE,
    title: 'Crew Change at Sea',
    description: 'Conducting crew transfers while at sea or at anchor.',
    commonHazards: [
      'Falls during transfer',
      'Man overboard',
      'Crush injuries',
      'Vessel motion injuries',
      'Equipment failures'
    ],
    suggestedControls: [
      'Weather limits observed',
      'Transfer equipment inspected',
      'Personnel wearing lifejackets',
      'Clear communication',
      'Emergency recovery ready'
    ],
    tier: 'lite',
    wbc3Reference: 'WBC3 Section 21'
  },

  // =========================================================================
  // MOORING & ANCHORING (6 tasks)
  // =========================================================================
  {
    id: 'WB-MOOR-001',
    category: TASK_CATEGORIES.MOORING_ANCHORING,
    title: 'Mooring Operations',
    description: 'Securing the vessel alongside using mooring lines.',
    commonHazards: [
      'Snap-back from parted lines',
      'Crush injuries from lines/equipment',
      'Falls on deck',
      'Hand injuries in fairleads',
      'Line handling injuries'
    ],
    suggestedControls: [
      'Snap-back zones marked and avoided',
      'Lines inspected before use',
      'PPE worn (gloves, safety footwear)',
      'Clear communication with bridge',
      'Adequate lighting'
    ],
    tier: 'lite',
    wbc3Reference: 'WBC3 Section 14'
  },
  {
    id: 'WB-MOOR-002',
    category: TASK_CATEGORIES.MOORING_ANCHORING,
    title: 'Anchoring Operations',
    description: 'Deploying and recovering the anchor.',
    commonHazards: [
      'Crush injuries from anchor/chain',
      'Falls on wet deck',
      'Anchor dragging',
      'Equipment failure',
      'Entanglement in chain'
    ],
    suggestedControls: [
      'Anchor cleared before deployment',
      'Personnel clear of chain run',
      'Brake tested before anchoring',
      'Good communication with bridge',
      'Anchor watch maintained'
    ],
    tier: 'lite',
    wbc3Reference: 'WBC3 Section 16'
  },
  {
    id: 'WB-MOOR-003',
    category: TASK_CATEGORIES.MOORING_ANCHORING,
    title: 'Single Point Mooring (SPM) Operations',
    description: 'Connecting to single point mooring buoys.',
    commonHazards: [
      'Line failures under tension',
      'Collision with buoy',
      'Personnel transfer risks',
      'Weather/sea state changes',
      'Hose handling hazards'
    ],
    suggestedControls: [
      'Approach procedures followed',
      'Pickup equipment inspected',
      'Weather limits observed',
      'Emergency release tested',
      'Communication with terminal'
    ],
    tier: 'lite',
    wbc3Reference: 'WBC3 Section 14.3'
  },
  {
    id: 'WB-MOOR-004',
    category: TASK_CATEGORIES.MOORING_ANCHORING,
    title: 'Working with Fenders',
    description: 'Deploying, adjusting, and recovering vessel fendering.',
    commonHazards: [
      'Manual handling injuries',
      'Crush injuries between vessel and structure',
      'Falls overboard',
      'Fender failures',
      'Line entanglement'
    ],
    suggestedControls: [
      'Correct size fenders for task',
      'Secure attachment points',
      'Two-person operation where needed',
      'PPE (gloves, lifejacket)',
      'Communication with bridge'
    ],
    tier: 'lite',
    wbc3Reference: 'WBC3 Section 14.2'
  },
  {
    id: 'WB-MOOR-005',
    category: TASK_CATEGORIES.MOORING_ANCHORING,
    title: 'Heaving Line Operations',
    description: 'Throwing and handling heaving lines for mooring.',
    commonHazards: [
      'Line whipping back',
      'Falls from reaching',
      'Struck by line/monkey fist',
      'Rope burns',
      'Slips on deck'
    ],
    suggestedControls: [
      'Clear throwing area',
      'Secure footing',
      'Gloves worn',
      'Communication with shore',
      'Practice throwing technique'
    ],
    tier: 'lite',
    wbc3Reference: 'WBC3 Section 14.1'
  },
  {
    id: 'WB-MOOR-006',
    category: TASK_CATEGORIES.MOORING_ANCHORING,
    title: 'Winch and Windlass Operations',
    description: 'Operating mooring winches and anchor windlass.',
    commonHazards: [
      'Entanglement in rotating parts',
      'Crush injuries',
      'Electric shock',
      'Hydraulic failures',
      'Remote control malfunction'
    ],
    suggestedControls: [
      'Equipment inspected before use',
      'Emergency stops identified',
      'Clear of rotating equipment',
      'Training completed',
      'Communication maintained'
    ],
    tier: 'lite',
    wbc3Reference: 'WBC3 Section 14.4'
  },

  // =========================================================================
  // CREW WELFARE (4 tasks)
  // =========================================================================
  {
    id: 'WB-CREW-001',
    category: TASK_CATEGORIES.CREW_WELFARE,
    title: 'Galley Operations',
    description: 'Food preparation and cooking operations.',
    commonHazards: [
      'Burns and scalds',
      'Knife cuts',
      'Slips on wet/oily surfaces',
      'Fire risks',
      'Food contamination'
    ],
    suggestedControls: [
      'Stove fiddles and guards in place',
      'Non-slip footwear',
      'Fire extinguisher nearby',
      'Food hygiene training',
      'Pot and pan securing at sea'
    ],
    tier: 'lite',
    wbc3Reference: 'WBC3 Section 24'
  },
  {
    id: 'WB-CREW-002',
    category: TASK_CATEGORIES.CREW_WELFARE,
    title: 'Housekeeping and Cleaning',
    description: 'General cleaning and maintenance of accommodation and work spaces.',
    commonHazards: [
      'Chemical exposure from cleaning agents',
      'Slips on wet surfaces',
      'Manual handling injuries',
      'Falls from steps/stairs',
      'Respiratory irritation'
    ],
    suggestedControls: [
      'Warning signs for wet floors',
      'PPE for chemical handling',
      'Proper ventilation',
      'Safe storage of chemicals',
      'Good lighting maintained'
    ],
    tier: 'lite',
    wbc3Reference: 'WBC3 Section 24'
  },
  {
    id: 'WB-CREW-003',
    category: TASK_CATEGORIES.CREW_WELFARE,
    title: 'Manual Handling and Stores',
    description: 'Loading, moving, and stowing stores and supplies.',
    commonHazards: [
      'Back injuries from lifting',
      'Crush injuries from falling items',
      'Slips and trips',
      'Restricted access injuries',
      'Repetitive strain'
    ],
    suggestedControls: [
      'Manual handling training',
      'Team lifting for heavy items',
      'Trolleys and lifting aids used',
      'Clear passageways',
      'Proper stowage arrangements'
    ],
    tier: 'lite',
    wbc3Reference: 'WBC3 Section 23.1'
  },
  {
    id: 'WB-CREW-004',
    category: TASK_CATEGORIES.CREW_WELFARE,
    title: 'Fatigue Management',
    description: 'Managing work/rest hours and fatigue risks.',
    commonHazards: [
      'Impaired judgment',
      'Increased accident risk',
      'Navigation errors',
      'Health deterioration',
      'Poor decision making'
    ],
    suggestedControls: [
      'Hours of rest recorded',
      'Watch schedules planned',
      'Rest periods protected',
      'Fatigue awareness training',
      'Reporting culture encouraged'
    ],
    tier: 'lite',
    wbc3Reference: 'WBC3 Section 22'
  },

  // =========================================================================
  // EMERGENCY DRILL SAFETY (4 tasks)
  // =========================================================================
  // NOTE: These risk assessments cover CONDUCTING EMERGENCY DRILLS SAFELY,
  // not responding to actual emergencies.
  //
  // Purpose:
  // - Identify risks to crew when practicing emergency procedures
  // - Plan safe drill conduct (WBC3 14.11 requires regular drills)
  // - Prevent injuries during training
  //
  // For actual emergency response procedures, see vessel's Emergency Response
  // Plan (required under WBC3 Section 31 SMS).
  //
  // Example: A "Fire Fighting Drill" risk assessment identifies that crew
  // could be burned practicing with fire extinguishers, so controls include:
  // qualified instructor, safety briefing, first aid kit, PPE.
  // =========================================================================
  {
    id: 'WB-DRILL-001',
    category: TASK_CATEGORIES.EMERGENCY_DRILL_SAFETY,
    title: 'Fire Fighting Drill',
    description: 'Conducting fire drills and training crew in fire fighting procedures safely, as required by WBC3 Section 14.11.',
    commonHazards: [
      'Burns during fire extinguisher training',
      'Smoke inhalation during drill scenarios',
      'Trips and falls during drill evacuation',
      'Crew fatigue from repeated drills',
      'Inadequate drill supervision leading to accidents'
    ],
    suggestedControls: [
      'Qualified instructor present during all drills',
      'Pre-drill safety briefing explaining procedures and stop signals',
      'First aid kit and trained first aider available',
      'PPE provided for all drill participants',
      'Drill area cleared of non-participants',
      'Maximum drill duration limits to prevent fatigue',
      'Post-drill debrief to identify safety issues'
    ],
    tier: 'lite',
    wbc3Reference: 'WBC3 Section 15 (Fire Safety) & Section 16 (Fire Appliances)'
  },
  {
    id: 'WB-DRILL-002',
    category: TASK_CATEGORIES.EMERGENCY_DRILL_SAFETY,
    title: 'Abandon Ship Drill',
    description: 'Conducting abandon ship drills and training crew in life-saving appliance deployment safely, as required by WBC3 Section 14.11.',
    commonHazards: [
      'Falls when entering survival craft during drill',
      'Crush injuries from liferaft launching mechanism',
      'Entanglement in davit wires during drill',
      'Crew injury during muster drill',
      'Equipment damage from improper drill handling'
    ],
    suggestedControls: [
      'Survival craft only partially deployed during drills (per manufacturer guidance)',
      'Crew briefed on davit operation hazards before drill',
      'Safety harnesses worn when working at height on davits',
      'Ground crew positioned clear of launching area',
      'Drill supervisor monitors all activities',
      'Maximum number of crew in survival craft during drill specified',
      'Weather conditions checked before launching drills'
    ],
    tier: 'lite',
    wbc3Reference: 'WBC3 Section 14 (Life-Saving Appliances)'
  },
  {
    id: 'WB-DRILL-003',
    category: TASK_CATEGORIES.EMERGENCY_DRILL_SAFETY,
    title: 'Man Overboard Drill',
    description: 'Conducting man overboard drills and practicing recovery procedures safely, as required by WBC3 Section 14.11.',
    commonHazards: [
      'Crew falling overboard during drill practice',
      'Injury from MOB dummy/equipment during recovery drill',
      'Line entanglement during drill',
      'Equipment damage from repeated drill use',
      'Inadequate drill briefing leading to confusion'
    ],
    suggestedControls: [
      'MOB dummy/mannequin used instead of actual person',
      'Recovery boat crew briefed on drill scenario',
      'Lifejackets worn by all participants during drill',
      'Recovery area monitored by safety observer',
      'Drill conducted in daylight and calm weather only',
      'All crew informed drill is in progress (not actual MOB)',
      'First aid kit available on deck during drill'
    ],
    tier: 'lite',
    wbc3Reference: 'WBC3 Section 14.7 (Recovery of Persons from Water)'
  },
  {
    id: 'WB-DRILL-004',
    category: TASK_CATEGORIES.EMERGENCY_DRILL_SAFETY,
    title: 'Medical Emergency Drill',
    description: 'Conducting medical emergency drills and training crew in first aid procedures safely, as required by WBC3 Section 23.',
    commonHazards: [
      'Injury during drill scenario practice',
      'Improper use of medical equipment during training',
      'Crew stress from realistic drill scenarios',
      'Cross-contamination during first aid practice',
      'Fatigue from lengthy drill exercises'
    ],
    suggestedControls: [
      'Realistic but safe drill scenarios (no actual injuries)',
      'Training manikins used for CPR practice',
      'Qualified first aid instructor supervises all training',
      'Hand hygiene and PPE used during practice',
      'Drill scenarios appropriate to crew experience level',
      'Debrief session after drill to reinforce learning',
      'Medical equipment checked before and after drills'
    ],
    tier: 'lite',
    wbc3Reference: 'WBC3 Section 23 (Medical Care & First Aid)'
  },

  // =========================================================================
  // CYBER SECURITY (WBC3 Section 31.3 - Mandatory)
  // =========================================================================
  {
    id: 'WB-CYBER-001',
    category: TASK_CATEGORIES.CYBER_SECURITY,
    title: 'Cyber Security Risk Assessment',
    description: 'Assessment of IT and OT systems, communication equipment, and remotely operable equipment for cyber risks.',
    commonHazards: [
      'Unauthorised access to navigation systems',
      'Malware infection of operational technology',
      'Ransomware attacks on vessel systems',
      'Spoofing of GPS/AIS signals',
      'Compromise of communication systems',
      'Loss of remote monitoring capability'
    ],
    suggestedControls: [
      'Network segmentation (IT/OT separation)',
      'Regular software updates and patching',
      'Strong password policies',
      'Multi-factor authentication where possible',
      'Regular backup of critical systems',
      'Crew cyber security training',
      'USB/removable media policy',
      'Incident response plan'
    ],
    tier: 'lite',
    wbc3Reference: 'WBC3 Section 31.3'
  },
  {
    id: 'WB-CYBER-002',
    category: TASK_CATEGORIES.CYBER_SECURITY,
    title: 'Remote Access Management',
    description: 'Managing and logging remote access to vessel systems as required by WBC3 31.3.5.',
    commonHazards: [
      'Unauthorised remote access',
      'Unlogged access attempts',
      'Compromised credentials',
      'Man-in-the-middle attacks',
      'Session hijacking'
    ],
    suggestedControls: [
      'Maintain log of authorised remote access systems',
      'Log all remote access occurrences',
      'Use encrypted connections (VPN)',
      'Limit remote access permissions',
      'Regular credential rotation',
      'Review access logs weekly'
    ],
    tier: 'lite',
    wbc3Reference: 'WBC3 Section 31.3.5'
  },
  {
    id: 'WB-CYBER-003',
    category: TASK_CATEGORIES.CYBER_SECURITY,
    title: 'Cyber Attack Recovery',
    description: 'Procedures for recovering vessel operations following a cyber attack, including safe haven procedures.',
    commonHazards: [
      'Loss of navigation capability',
      'Loss of propulsion control',
      'Communication system failure',
      'Inability to reach safe haven',
      'Data loss',
      'Extended downtime'
    ],
    suggestedControls: [
      'Back-up plan to reach safe haven (WBC3 31.3.6)',
      'Back-up files stored on board vessel',
      'Manual override procedures for critical systems',
      'Paper-based navigation capability',
      'Alternative communication methods',
      'Regular recovery drills',
      'Offline backup of critical data'
    ],
    tier: 'lite',
    wbc3Reference: 'WBC3 Section 31.3.6'
  }
];

/**
 * Get tasks by category
 * @param {string} category - Category name
 * @returns {Array} Tasks in category
 */
export const getTasksByCategory = (category) => {
  return workboatTasks.filter(task => task.category === category);
};

/**
 * Get task by ID
 * @param {string} id - Task ID
 * @returns {Object|null} Task or null
 */
export const getTaskById = (id) => {
  return workboatTasks.find(task => task.id === id) || null;
};

/**
 * Get all categories with task counts
 * @returns {Array} Categories with counts
 */
export const getCategoriesWithCounts = () => {
  const categories = {};

  workboatTasks.forEach(task => {
    if (!categories[task.category]) {
      categories[task.category] = 0;
    }
    categories[task.category]++;
  });

  return Object.entries(categories).map(([category, count]) => ({
    category,
    count
  }));
};

/**
 * Search tasks by keyword
 * @param {string} query - Search query
 * @returns {Array} Matching tasks
 */
export const searchTasks = (query) => {
  const lowerQuery = query.toLowerCase();

  return workboatTasks.filter(task =>
    task.title.toLowerCase().includes(lowerQuery) ||
    task.description.toLowerCase().includes(lowerQuery) ||
    task.commonHazards.some(h => h.toLowerCase().includes(lowerQuery)) ||
    task.suggestedControls.some(c => c.toLowerCase().includes(lowerQuery))
  );
};
